import type { Idioma } from '../../i18n/idioma';

/**
 * Cloudflare Turnstile no pré-registro (techspec-landing-fase §04.3 e §06.3).
 * O script entra só aqui, só quando o estado trouxe a chave, e o desafio roda no envio
 * (`execution: 'execute'`), invisível a menos que a Cloudflare peça interação.
 */
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const ACAO = 'pre-registro';
const LIMITE_MS = 30_000;

interface Turnstile {
  render(el: HTMLElement, opcoes: Record<string, unknown>): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window { turnstile?: Turnstile }
}

export interface AntiRobo {
  /** Roda o desafio e devolve o token (uso único, vale 5 min). */
  obterToken(): Promise<string>;
  /** Depois de cada envio: o token já foi gasto. */
  reiniciar(): void;
  remover(): void;
}

let carregando: Promise<Turnstile> | null = null;

function carregarTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  carregando ??= new Promise<Turnstile>((ok, falha) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? ok(window.turnstile) : falha(new Error('turnstile')));
    s.onerror = () => {
      carregando = null;
      s.remove();
      falha(new Error('turnstile'));
    };
    document.head.appendChild(s);
  });
  return carregando;
}

const LINGUA: Readonly<Record<Idioma, string>> = { 'pt-BR': 'pt-br', 'en-US': 'en', 'es-ES': 'es' };

export async function montarAntiRobo(el: HTMLElement, chaveSite: string, idioma: Idioma): Promise<AntiRobo> {
  const ts = await carregarTurnstile();
  let pendente: { ok: (token: string) => void; falha: (e: Error) => void } | null = null;
  const resolver = (token: string | null) => {
    const p = pendente;
    pendente = null;
    if (!p) return;
    if (token) p.ok(token);
    else p.falha(new Error('antiRobo'));
  };

  const id = ts.render(el, {
    sitekey: chaveSite,
    action: ACAO,
    appearance: 'interaction-only',
    execution: 'execute',
    theme: 'dark',
    size: 'flexible',
    language: LINGUA[idioma],
    callback: (token: string) => resolver(token),
    'error-callback': () => { resolver(null); return true; },
    'timeout-callback': () => resolver(null),
    'expired-callback': () => ts.reset(id)
  });

  return {
    obterToken: () => new Promise<string>((ok, falha) => {
      const limite = window.setTimeout(() => resolver(null), LIMITE_MS);
      pendente = {
        ok: token => { window.clearTimeout(limite); ok(token); },
        falha: e => { window.clearTimeout(limite); falha(e); }
      };
      try {
        ts.execute(id);
      } catch {
        resolver(null);
      }
    }),
    reiniciar: () => ts.reset(id),
    remover: () => ts.remove(id)
  };
}
